<template>
  <div class="brand-guidelines">
    <div v-if="!isEditMode">
      <div class="d-flex align-items-center gap-4 mb-4 justify-content-between">
        <div class="search-wrap flex-grow-1 position-relative">
          <svg
            class="search-icon"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#aaa"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            class="form-control search-input"
            placeholder="Search brand guidelines..."
          >
        </div>
        <button
          :class="isCreativeStudio ? 'cs-lib-btn cs-lib-btn--newseq' : 'btn btn-teal d-flex align-items-center btn-library'"
          @click="openNewBrandGuideline"
        >
          <i class="bi bi-plus-lg mr-1" />
          <span>New Brand Guidelines</span>
        </button>
      </div>

      <div class="seq-list">
        <div
          v-for="seq in filteredBrandGuidelines"
          :key="seq.id"
          class="seq-row d-flex align-items-center justify-content-between"
          @mouseenter="hoveredId = seq.id"
          @mouseleave="hoveredId = null"
        >
          <div class="seq-info">
            <div
              class="seq-thumb"
              :class="{ 'seq-thumb--empty': !brandGuidelineThumbnailUrl(seq) }"
            >
              <img
                v-if="brandGuidelineThumbnailUrl(seq)"
                :src="brandGuidelineThumbnailUrl(seq)"
                :alt="seq.brand_name"
              >
              <svg
                v-else
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  width="18"
                  height="18"
                  x="3"
                  y="3"
                  rx="2"
                  ry="2"
                />
                <circle
                  cx="9"
                  cy="9"
                  r="2"
                />
                <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
              </svg>
            </div>
            <div>
              <div class="d-flex align-items-center gap-2 mb-1">
                <span class="seq-name">{{ seq.brand_name }}</span>
                <span
                  v-if="seq.voice"
                  class="scope-tag"
                >
                  {{ seq.voice }}
                </span>
                <span
                  v-if="seq.visual_style"
                  class="scope-tag"
                >
                  {{ seq.visual_style }}
                </span>
              </div>
              <div class="seq-slots">
                <div class="preview-meta d-flex align-items-center gap-2">
                  <span class="meta-item">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      class="mr-1"
                    >
                      <path
                        d="M9 1H3a1 1 0 00-1 1v8a1 1 0 001 1h6a1 1 0 001-1V2a1 1 0 00-1-1z"
                        stroke="currentColor"
                        stroke-width="1.2"
                        fill="none"
                      />
                      <path
                        d="M4 4h4M4 6h4M4 8h2"
                        stroke="currentColor"
                        stroke-width="1"
                        stroke-linecap="round"
                      />
                    </svg>
                    {{ seq.documents_count || seq.docs_count || 0 }} docs
                  </span>
                  <span class="meta-item">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      class="mr-1"
                    >
                      <rect
                        x="1"
                        y="2"
                        width="10"
                        height="8"
                        rx="1"
                        stroke="currentColor"
                        stroke-width="1.2"
                        fill="none"
                      />
                      <path
                        d="M1 7.5l2.5-2.5 2 2L8 4l3 3.5"
                        stroke="currentColor"
                        stroke-width="1"
                        stroke-linecap="round"
                      />
                    </svg>
                    {{ seq.images_count || 0 }} images
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!isCreativeStudio && !fromAnalysis"
            class="seq-actions d-flex align-items-center gap-1"
            :class="{ visible: hoveredId === seq.id }"
          >
            <button
              class="icon-btn"
              @click="handleBrandGuidelineEdit(seq)"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 20h9" />
                <path
                  d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"
                />
              </svg>
            </button>
            <button
              class="icon-btn icon-btn-danger"
              @click="handleDelete(seq.id)"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </button>
          </div>
          <div
            v-if="isCreativeStudio || fromAnalysis"
            class="seq-actions d-flex align-items-center gap-2"
            :class="{ visible: hoveredId === seq.id }"
          >
            <button
              type="button"
              class="cs-lib-btn cs-lib-btn--primary cs-lib-btn--use"
              @click="useBrandGuideline(seq)"
            >
              Use
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="isLoadingBrandGuidelines"
        class="brand-guidelines-loading"
      >
        <b-spinner
          small
          label="Loading..."
        />
        <span class="ml-2">Loading brand guidelines...</span>
      </div>

      <div
        v-if="brandGuidelines.length === 0 && !isLoadingBrandGuidelines"
        class="empty-state-panel"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="empty-state-panel__icon"
        >
          <circle
            cx="13.5"
            cy="6.5"
            r=".5"
            fill="currentColor"
          />
          <circle
            cx="17.5"
            cy="10.5"
            r=".5"
            fill="currentColor"
          />
          <circle
            cx="8.5"
            cy="7.5"
            r=".5"
            fill="currentColor"
          />
          <circle
            cx="6.5"
            cy="12.5"
            r=".5"
            fill="currentColor"
          />
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
        </svg>
        <div class="empty-state-panel__title">
          No brand guidelines yet
        </div>
        <p class="empty-state-panel__desc">
          Click "New Brand Guidelines" to get started.
        </p>
      </div>

      <div
        v-else-if="filteredBrandGuidelines.length === 0 && !isLoadingBrandGuidelines"
        class="empty-state-search"
      >
        No brand guidelines found
      </div>
    </div>
    <div
      v-if="isEditMode"
      class="content-area"
    >
      <div class="edit-top-bar">
        <button
          type="button"
          class="btn-edit-back"
          @click="cancel"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
          </svg>
          Back
        </button>
        <button
          type="button"
          class="btn-edit-save"
          :disabled="saveButtonDisabled"
          @click="save"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          Save
        </button>
      </div>

      <div class="edit-grid">
        <div class="edit-main">
          <div
            v-if="!assistantMode"
            class="edit-form"
          >
            <div class="form-group">
              <label
                class="field-label"
                for="brand-name"
              >Brand name</label>
              <input
                id="brand-name"
                v-model="form.brandName"
                type="text"
                class="form-control field-input"
                placeholder="e.g. Marie Callender's — Frozen"
              >
            </div>

            <div class="form-group">
              <label class="field-label">Thumbnail</label>
              <ImageGroupUploadZone
                variant="lovable"
                :multiple="false"
                @add-files="addThumbnailFiles"
              />
              <div class="field-hint">
                Shown next to the brand name in the library list.
              </div>
            </div>

            <div class="form-group">
              <div class="assets-label-row">
                <label class="field-label mb-0">Brand assets</label>
                <span
                  v-if="processingFileCount > 0"
                  class="assets-processing-label"
                >
                  <b-spinner
                    small
                    class="mr-1"
                  />
                  Parsing {{ processingFileCount }} file{{ processingFileCount === 1 ? '' : 's' }}...
                </span>
                <span
                  v-else-if="needsBrandNameForAssets"
                  class="assets-brandname-hint"
                >
                  Add a brand name to enable parsing
                </span>
              </div>

              <div
                class="upload-dropzone"
                :class="{ 'upload-dropzone--active': isDragOver }"
                @click="openBrandAssetsPicker"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleBrandAssetsDrop"
              >
                <svg
                  class="upload-dropzone__icon"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line
                    x1="12"
                    x2="12"
                    y1="3"
                    y2="15"
                  />
                </svg>
                <div class="upload-dropzone__title">
                  Drop brand docs or images here
                </div>
                <div class="upload-dropzone__hint">
                  PDF, DOCX, PPTX, images — we'll extract guidelines automatically
                </div>
              </div>

              <input
                ref="brandAssetsInput"
                type="file"
                class="d-none"
                multiple
                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.rtf,image/*"
                @change="handleBrandAssetsSelected"
              >

              <div
                v-if="uploadedAssets.length"
                class="asset-list"
              >
                <div
                  v-for="(asset, index) in uploadedAssets"
                  :key="getAttachmentKey(asset, index, asset.type)"
                  class="asset-row"
                >
                  <div class="asset-row__icon">
                    <svg
                      v-if="asset.type === 'document'"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                    </svg>
                    <img
                      v-else-if="asset.url"
                      :src="asset.url"
                      :alt="asset.name"
                      class="asset-row__thumb"
                    >
                    <svg
                      v-else
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect
                        width="18"
                        height="18"
                        x="3"
                        y="3"
                        rx="2"
                        ry="2"
                      />
                      <circle
                        cx="9"
                        cy="9"
                        r="2"
                      />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                  </div>
                  <div class="asset-row__info">
                    <div class="asset-row__name">
                      {{ asset.name }}
                    </div>
                    <div
                      v-if="asset.size"
                      class="asset-row__size"
                    >
                      {{ formatFileSize(asset.size) }}
                    </div>
                  </div>
                  <div class="asset-row__actions">
                    <span
                      v-if="asset.status === 'parsing'"
                      class="asset-badge asset-badge--processing"
                    >
                      <b-spinner
                        small
                        class="mr-1"
                      />
                      Parsing
                    </span>
                    <template v-else>
                      <span
                        class="asset-badge"
                        :class="asset.status === 'failed' ? 'asset-badge--failed' : 'asset-badge--ready'"
                      >
                        <svg
                          v-if="asset.status !== 'failed'"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                        {{ asset.status === 'failed' ? 'Failed' : 'Ready' }}
                      </span>
                      <button
                        type="button"
                        class="btn-insert"
                        :disabled="asset.status === 'parsing' || !hasBrandName"
                        :title="!hasBrandName ? 'Enter a brand name first' : null"
                        @click="insertExtractedContent(asset)"
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                          <path d="M20 3v4" />
                          <path d="M22 5h-4" />
                          <path d="M4 17v2" />
                          <path d="M5 18H3" />
                        </svg>
                        {{ hasExtractedSection(asset.name) ? 'Insert again' : 'Insert' }}
                      </button>
                    </template>
                    <button
                      type="button"
                      class="asset-row__remove"
                      title="Remove"
                      @click="removeBrandAsset(index)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <div class="guidelines-label-row">
                <label
                  class="field-label mb-0"
                  for="brand-guidelines"
                >Guidelines</label>
                <button
                  type="button"
                  class="btn-use-template"
                  @click="useGuidelinesTemplate"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                    <path d="M20 3v4" />
                    <path d="M22 5h-4" />
                    <path d="M4 17v2" />
                    <path d="M5 18H3" />
                  </svg>
                  Use template
                </button>
              </div>
              <textarea
                id="brand-guidelines"
                v-model="form.guidelines"
                class="form-control field-textarea field-textarea--mono"
                :placeholder="guidelinesPlaceholder"
                rows="12"
              />
              <div class="field-hint">
                Free-form — paste, type, or let uploads fill this in. Edit anything that comes out of processing.
              </div>
            </div>
          </div>

          <input
            ref="documentsInput"
            type="file"
            class="d-none"
            multiple
            accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.ppt,.pptx"
            @change="handleDocumentsSelected"
          >

          <input
            ref="imagesInput"
            type="file"
            class="d-none"
            multiple
            accept="image/*"
            @change="handleImagesSelected"
          >
          <div
            v-if="assistantMode"
            class="bga-shell"
          >
            <div class="bga-header">
              <div class="bga-header__left">
                <span class="bga-header__icon">
                  <i class="bi bi-stars" />
                </span>
                <span class="bga-header__title">Brand Guidelines Assistant</span>
                <span class="bga-header__badge">Beta</span>
              </div>

              <div class="bga-header__right">
                <span class="bga-btn--ready-save">{{ assistantStepLabel }}</span>
                <button
                  class="bga-btn bga-btn--outline"
                  style="border: none;"
                  @click="assistantMode = false"
                >
                  Use form
                </button>
              </div>
            </div>

            <div
              ref="messageList"
              class="bga-messages"
            >
              <transition-group
                name="bga-msg"
                tag="div"
                class="bga-messages__inner"
              >
                <div
                  v-for="msg in messages"
                  :key="msg.id"
                  class="bga-msg"
                  :class="`bga-msg--${msg.type}`"
                >
                  <template v-if="msg.type === 'assistant'">
                    <div class="bga-msg__bubble">
                      <span class="bga-msg__text">
                        {{ msg.text }}
                      </span>
                    </div>
                  </template>

                  <template v-else-if="msg.type === 'user'">
                    <div class="bga-msg__bubble">
                      <p class="bga-msg__text">
                        {{ msg.text }}
                      </p>
                      <!-- Attached files -->
                      <div
                        v-if="msg.files && msg.files.length"
                        class="bga-msg__files"
                      >
                        <div
                          v-for="(f, fi) in msg.files"
                          :key="fi"
                          class="bga-file-chip"
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 11 11"
                            fill="none"
                          >
                            <path
                              d="M2 1h5l3 3v6a1 1 0 01-1 1H2a1 1 0 01-1-1V2a1 1 0 011-1z"
                              stroke="currentColor"
                              stroke-width="1.2"
                              fill="none"
                            />
                            <path
                              d="M7 1v3h3"
                              stroke="currentColor"
                              stroke-width="1.2"
                              fill="none"
                            />
                          </svg>
                          {{ f.name }}
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-else-if="msg.type === 'typing'">
                    <div class="bga-msg__bubble bga-msg__bubble--typing">
                      <span class="bga-typing-dot" />
                      <span class="bga-typing-dot" />
                      <span class="bga-typing-dot" />
                    </div>
                  </template>
                </div>
              </transition-group>
            </div>

            <div class="bga-input-bar">
              <button
                class="bga-tool-btn"
                title="Attach file"
                @click="triggerFileInput"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M13.5 7.5L7 14a4 4 0 01-5.66-5.66l7-7a2.5 2.5 0 013.54 3.54L5.4 11.35a1 1 0 01-1.41-1.41L10 4"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <button
                class="bga-tool-btn"
                title="Attach image"
                @click="triggerImageInput"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <rect
                    x="1"
                    y="2"
                    width="14"
                    height="12"
                    rx="1.5"
                    stroke="currentColor"
                    stroke-width="1.4"
                    fill="none"
                  />
                  <path
                    d="M1 10.5l4-4 3.5 3.5L12 6l3 4.5"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                  <circle
                    cx="5.5"
                    cy="6.5"
                    r="1.2"
                    fill="currentColor"
                  />
                </svg>
              </button>

              <div class="bga-input-wrap">
                <textarea
                  ref="textarea"
                  v-model="inputText"
                  class="bga-textarea"
                  :placeholder="inputPlaceholder"
                  rows="1"
                  @input="autoResize"
                  @keydown.enter.exact.prevent="send"
                  @keydown.enter.shift.exact="newline"
                />
              </div>

              <button
                title="Send"
                class="bga-send-btn"
                :class="{ 'bga-send-btn--active': canSend }"
                :disabled="!canSend"
                @click="send"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path
                    d="M13 7.5H2M13 7.5L8.5 3M13 7.5L8.5 12"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <aside class="live-preview">
          <div class="live-preview__card">
            <div class="live-preview__header">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-primary"
              >
                <circle
                  cx="13.5"
                  cy="6.5"
                  r=".5"
                  fill="currentColor"
                />
                <circle
                  cx="17.5"
                  cy="10.5"
                  r=".5"
                  fill="currentColor"
                />
                <circle
                  cx="8.5"
                  cy="7.5"
                  r=".5"
                  fill="currentColor"
                />
                <circle
                  cx="6.5"
                  cy="12.5"
                  r=".5"
                  fill="currentColor"
                />
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
              </svg>
              <span class="live-preview__title">Live preview</span>
              <span class="live-preview__badge">Auto</span>
            </div>

            <div class="live-preview__body">
              <div class="live-preview__section">
                <div class="live-preview__label">
                  Brand
                </div>
                <div
                  class="live-preview__brand"
                  :class="{ 'live-preview__brand--empty': !form.brandName.trim() }"
                >
                  {{ previewBrandName }}
                </div>
              </div>

              <div class="live-preview__section">
                <div class="live-preview__label live-preview__label--row">
                  Logo / hero image
                  <button
                    v-if="thumbnailPreviewUrl"
                    type="button"
                    class="thumbnail-remove-btn"
                    @click="clearThumbnailSelection"
                  >
                    Remove
                    <b-icon
                      icon="x"
                      font-scale="1.4"
                    />
                  </button>
                </div>
                <div
                  v-if="previewHeroImage"
                  class="live-preview__hero"
                >
                  <img
                    :src="previewHeroImage"
                    alt="Brand hero"
                  >
                </div>
                <div
                  v-else
                  class="live-preview__hero live-preview__hero--empty"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect
                      width="18"
                      height="18"
                      x="3"
                      y="3"
                      rx="2"
                      ry="2"
                    />
                    <circle
                      cx="9"
                      cy="9"
                      r="2"
                    />
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                  </svg>
                  <span>Upload an image to preview</span>
                </div>
              </div>

              <div class="live-preview__section">
                <div class="live-preview__label">
                  Colors
                </div>
                <div
                  v-if="previewColors.length"
                  class="live-preview__colors"
                >
                  <div
                    v-for="color in previewColors"
                    :key="color"
                    class="live-preview__color"
                  >
                    <span
                      class="live-preview__swatch"
                      :style="{ backgroundColor: color }"
                    />
                    <span class="live-preview__hex">{{ color }}</span>
                  </div>
                </div>
                <div
                  v-else
                  class="live-preview__empty-hint"
                >
                  Mention hex codes (e.g. #1E2761) and swatches appear here.
                </div>
              </div>

              <div class="live-preview__section">
                <div class="live-preview__label">
                  Typography
                </div>
                <div
                  v-if="previewTypography.length"
                  class="live-preview__typography"
                >
                  <div
                    v-for="(line, index) in previewTypography"
                    :key="`typo-${index}`"
                    class="live-preview__typo-line"
                  >
                    {{ line }}
                  </div>
                </div>
                <div
                  v-else
                  class="live-preview__empty-hint"
                >
                  Add a "Typography" section to preview fonts.
                </div>
              </div>

              <div class="live-preview__stats">
                <span class="meta-item">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="mr-1"
                  >
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  </svg>
                  {{ docsCount }} docs
                </span>
                <span class="meta-item">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="mr-1"
                  >
                    <rect
                      width="18"
                      height="18"
                      x="3"
                      y="3"
                      rx="2"
                      ry="2"
                    />
                    <circle
                      cx="9"
                      cy="9"
                      r="2"
                    />
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                  </svg>
                  {{ imagesCount }} images
                </span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      <div class="edit-sticky-footer">
        <div class="edit-sticky-footer__inner">
          <div class="edit-sticky-footer__hint">
            Upload brand assets or type guidelines, then save.
          </div>
          <div class="edit-sticky-footer__actions">
            <button
              type="button"
              class="btn-footer-cancel"
              @click="cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn-footer-save"
              :disabled="saveButtonDisabled"
              @click="save"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Save brand guidelines
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  findBrandGuidelineBySlug,
  getBrandGuidelineSlug,
} from '@frontend/utils/brandGuidelineSlug';
import {
  brandGuidelineThumbnailUrl as resolveBrandGuidelineThumbnailUrl,
} from '@frontend/utils/brand-guideline-utils';
import ImageGroupUploadZone from '@frontend/components/image-groups/ImageGroupUploadZone.vue';
import { partitionImagesBySize } from '@frontend/components/image-groups/image-group-utils';

const GUIDELINES_PLACEHOLDER = `# Brand Colors
Primary, secondary, accents (hex / Pantone)…

# Typography
Heading + body font families, weights, sizing rules…

# Logo Usage
Clear space, min size, do's & don'ts…

# Voice & Tone
How the brand sounds — adjectives, example phrasing…

# Visual Style
Photography direction, illustration, iconography…

# Do's & Don'ts
Quick rules to keep things on-brand…

# Other Notes
Anything else worth knowing…`;

export default {
  name: "BrandGuidelines",
  components: {
    ImageGroupUploadZone,
  },
  props: {
    isCreativeStudio: {
      type: Boolean,
      default: false,
    },
    fromAnalysis: {
      type: Boolean,
      default: false,
    },
    urlBrandSlug: {
      type: String,
      default: null,
    },
    syncUrl: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoadingBrandGuidelines: false,
      isEditMode: false,
      searchQuery: "",
      hoveredId: null,
      brandGuidelines: [],
      selectedBrandGuideline: null,
      form: {
        brandName: '',
        voice: '',
        visualStyle: '',
        guidelines: '',
        docs: 0,
        images: 0,
      },
      uploadedAssets: [],
      processingTimeouts: {},
      activeParseRequests: {},
      brandGuidelinesSocketChannel: null,
      isDragOver: false,
      guidelinesPlaceholder: GUIDELINES_PLACEHOLDER,
      deletedAttachmentIds: [],
      isSaving: false,
      assistantMode: false,
      inputText: '',
      isReadyToSave: false,
      messages: [],
      assistantStep: 'brand_name',
      assistantLoading: false,
      skippedAssistantSteps: [],
      messageId: 0,
      companyId: window.companyId,
      hasSocketGuidelinesUpdate: false,
      pendingDraftSave: null,
      thumbnailFile: null,
      thumbnailPreviewUrl: null,
      hadExistingThumbnail: false,
    };
  },
  computed: {
    saveButtonDisabled() {
      return !this.canSave;
    },

    canSave() {
      if (this.isSaving) {
        return false;
      }

      if (this.processingFileCount > 0) {
        return false;
      }

      if (!this.form.brandName || !this.form.brandName.trim()) {
        return false;
      }

      return this.hasFormChanges;
    },

    removeThumbnail() {
      return this.hadExistingThumbnail && !this.thumbnailFile && !this.thumbnailPreviewUrl;
    },

    hasFormChanges() {
      const hasNewAssets = this.uploadedAssets.some((asset) => !asset.isExisting);

      if (this.hasSocketGuidelinesUpdate) {
        return true;
      }

      // Create mode
      if (!this.selectedBrandGuideline) {
        return Boolean(
          this.form.brandName.trim() ||
          this.form.guidelines.trim() ||
          hasNewAssets ||
          this.thumbnailFile ||
          this.removeThumbnail
        );
      }

      // Edit mode
      const original = this.selectedBrandGuideline;

      return (
        this.form.brandName !== (original.brand_name || original.name || '') ||
        this.form.guidelines !== (original.guidelines || '') ||
        hasNewAssets ||
        this.deletedAttachmentIds.length > 0 ||
        this.thumbnailFile ||
        this.removeThumbnail
      );
    },
    processingFileCount() {
      return this.uploadedAssets.filter((asset) => asset.status === 'parsing').length;
    },
    hasBrandName() {
      return Boolean(this.form.brandName && this.form.brandName.trim());
    },
    needsBrandNameForAssets() {
      return this.uploadedAssets.length > 0 && !this.hasBrandName;
    },
    docsCount() {
      return this.uploadedAssets.filter((asset) => asset.type === 'document').length;
    },
    imagesCount() {
      return this.uploadedAssets.filter((asset) => asset.type === 'image').length;
    },
    previewBrandName() {
      return this.form.brandName.trim() || 'Untitled';
    },
    previewHeroImage() {
      if (this.thumbnailPreviewUrl) {
        return this.thumbnailPreviewUrl;
      }
      const hero = this.uploadedAssets.find((asset) => asset.type === 'image' && asset.url);
      return hero ? hero.url : null;
    },
    previewColors() {
      const matches = (this.form.guidelines || '').match(/#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/g) || [];

      const normalized = matches.map((color) => {
        const hex = color.toUpperCase();
        return hex.length === 4
          ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
          : hex;
      });

      return [...new Set(normalized)].slice(0, 12);
    },
    previewTypography() {
      const text = this.form.guidelines || '';
      const sectionRegex = /(?:^|\n)#{1,3}\s*Typography[^\n]*\n([\s\S]*?)(?=\n#{1,3}\s|$)/gi;
      const lines = [];
      let match;

      while ((match = sectionRegex.exec(text)) !== null) {
        match[1]
          .split('\n')
          .map((line) => line.replace(/^[-*]\s*/, '').trim())
          .filter(Boolean)
          .forEach((line) => {
            if (!lines.includes(line)) {
              lines.push(line);
            }
          });
      }

      return lines.slice(0, 6);
    },
    filteredBrandGuidelines() {
      const q = this.searchQuery.toLowerCase().trim();

      if (!q) {
        return this.brandGuidelines;
      }

      return this.brandGuidelines.filter((guideline) => {
        return [
          guideline.brand_name,
          guideline.voice,
          guideline.visual_style,
          guideline.guidelines,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(q));
      });
    },
    attachmentCount() {
      return `${this.docsCount} docs · ${this.imagesCount} images`;
    },
    inputPlaceholder() {
      return 'Add more details, or hit Save →';
    },
    canSend() {
      return !this.assistantLoading && this.assistantMode;
    },
    assistantStepLabel() {
      const labels = {
        edit_choice: 'Choose field',
        edit_more: 'Update more?',
        brand_name: 'Step: Brand',
        voice: 'Step: Voice',
        visual_style: 'Step: Style',
        guidelines: 'Step: Guidelines',
        attachments: 'Step: Attachments',
        generating: 'Generating',
        ready: 'Ready To Save',
      };

      return labels[this.assistantStep] || 'Choose field';
    },
  },
  watch: {
    isEditMode(isEditMode) {
      this.$emit('edit-mode-change', isEditMode);
    },
    urlBrandSlug(newSlug) {
      if (!this.syncUrl) {
        return;
      }

      if (newSlug) {
        this.openBrandFromUrlSlug(newSlug);
        return;
      }

      if (this.isEditMode) {
        this.cancel({ skipUrlSync: true });
      }
    },
  },
  created() {
    this.subscribeToBrandGuidelinesParsingChannel();
  },
  async mounted() {
    await this.fetchBrandGuidelines();
  },
  beforeDestroy() {
    Object.values(this.processingTimeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    this.unsubscribeFromBrandGuidelinesParsingChannel();

    this.uploadedAssets.forEach((asset) => {
      if (!asset.isExisting && asset.url) {
        URL.revokeObjectURL(asset.url);
      }
    });

    this.clearThumbnailSelection();
  },
  methods: {
    brandGuidelineThumbnailUrl(guideline) {
      return resolveBrandGuidelineThumbnailUrl(guideline);
    },

    addThumbnailFiles(files) {
      const { valid } = partitionImagesBySize(files);
      const file = valid[0];

      if (!file) {
        return;
      }

      this.clearThumbnailSelection();
      this.thumbnailFile = file;
      this.thumbnailPreviewUrl = URL.createObjectURL(file);

      const alreadyInAssets = this.uploadedAssets.some(
        (asset) => asset.name === file.name && asset.size === file.size
      );

      if (!alreadyInAssets) {
        this.addBrandAsset(file);
      }
    },

    resetThumbnailState() {
      this.clearThumbnailSelection();
      this.hadExistingThumbnail = false;
    },

    clearThumbnailSelection() {
      if (this.thumbnailPreviewUrl && this.thumbnailFile) {
        URL.revokeObjectURL(this.thumbnailPreviewUrl);
      }

      this.thumbnailFile = null;
      this.thumbnailPreviewUrl = null;
    },

    loadThumbnailFromGuideline(brandGuideline) {
      this.resetThumbnailState();

      if (brandGuideline?.thumbnail_url || brandGuideline?.thumbnail) {
        this.thumbnailPreviewUrl = resolveBrandGuidelineThumbnailUrl(brandGuideline, { lightweight: false });
        this.hadExistingThumbnail = true;
      }
    },

    appendThumbnailToFormData(formData) {
      if (this.thumbnailFile) {
        formData.append('thumbnail', this.thumbnailFile);
      }

      if (this.removeThumbnail) {
        formData.append('remove_thumbnail', '1');
      }
    },

    async fetchBrandGuidelines() {
      this.isLoadingBrandGuidelines = true;
      try {
        const result = await axios.get('/brand-guidelines');

        if (result.status === 200) {
          this.brandGuidelines = result.data.data || result.data;
        }

        if (this.syncUrl && this.urlBrandSlug) {
          this.openBrandFromUrlSlug(this.urlBrandSlug);
        }
      } catch (error) {
        console.error('Failed to load brand guidelines', error);
      } finally {
        this.isLoadingBrandGuidelines = false;
      }
    },
    isEditingExistingRecord() {
      return !!(this.selectedBrandGuideline && this.selectedBrandGuideline.id);
    },
    handleBrandGuidelineEdit(brandGuideline, options = {}) {
      this.selectedBrandGuideline = brandGuideline;

      this.form = {
        brandName: brandGuideline.brand_name || brandGuideline.name || '',
        voice: brandGuideline.voice || '',
        visualStyle: brandGuideline.visual_style || '',
        guidelines: brandGuideline.guidelines || '',
        docs: 0,
        images: 0,
      };

      this.loadSavedAttachments(brandGuideline.attachments || []);
      this.loadThumbnailFromGuideline(brandGuideline);

      this.isEditMode = true;

      if (!options.skipUrlSync) {
        this.syncBrandGuidelineUrl();
      }
    },
    openBrandFromUrlSlug(slug) {
      if (!slug || !this.brandGuidelines.length) {
        return;
      }

      const brandGuideline = findBrandGuidelineBySlug(this.brandGuidelines, slug);

      if (!brandGuideline) {
        return;
      }

      if (
        this.isEditMode &&
        this.selectedBrandGuideline?.id === brandGuideline.id
      ) {
        return;
      }

      this.handleBrandGuidelineEdit(brandGuideline, { skipUrlSync: true });
    },
    syncBrandGuidelineUrl() {
      if (!this.syncUrl) {
        return;
      }

      if (!this.isEditMode) {
        this.$emit('url-change', null);
        return;
      }

      if (!this.selectedBrandGuideline?.id) {
        this.$emit('url-change', null);
        return;
      }

      const slug = getBrandGuidelineSlug(this.selectedBrandGuideline, this.brandGuidelines);

      this.$emit('url-change', slug);
    },
    addDocuments() {
      this.$refs.documentsInput.click();
    },

    addImages() {
      this.$refs.imagesInput.click();
    },

    openBrandAssetsPicker() {
      this.$refs.brandAssetsInput?.click();
    },

    handleBrandAssetsDrop(event) {
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer?.files || []);
      files.forEach((file) => this.addBrandAsset(file));
    },

    handleBrandAssetsSelected(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => this.addBrandAsset(file));
      event.target.value = '';
    },

    isImageFile(file) {
      return (file.type && file.type.startsWith('image/')) || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(file.name);
    },

    addBrandAsset(file) {
      const isImage = this.isImageFile(file);
      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      this.uploadedAssets.push({
        id: null,
        localId,
        name: file.name,
        size: file.size,
        url: isImage ? URL.createObjectURL(file) : null,
        file,
        isExisting: false,
        type: isImage ? 'image' : 'document',
        mimeType: file.type || '',
        status: 'ready',
        parseError: '',
        parseRequestId: null,
      });

      if (this.assistantMode) {
        this.pushMessage(
          'assistant',
          `Added ${file.name}.`
        );
      }
    },

    simulateAssetProcessing(localId) {
      const delay = 1800 + Math.floor(Math.random() * 1200);

      this.processingTimeouts[localId] = window.setTimeout(() => {
        const asset = this.uploadedAssets.find((item) => item.localId === localId);

        if (asset && asset.status === 'processing') {
          asset.status = 'ready';
        }

        delete this.processingTimeouts[localId];
      }, delay);
    },

    formatFileSize(bytes) {
      if (!bytes) return '';

      const units = ['B', 'KB', 'MB', 'GB'];
      let size = bytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
      }

      return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
    },

    useGuidelinesTemplate() {
      if (!this.form.guidelines.trim()) {
        this.form.guidelines = GUIDELINES_PLACEHOLDER;
        return;
      }

      this.form.guidelines = `${this.form.guidelines.trim()}\n\n${GUIDELINES_PLACEHOLDER}`;
    },

    hasExtractedSection(fileName) {
      return (this.form.guidelines || '').includes(`## Extracted from ${fileName}`);
    },

    extractedSectionRegex(fileName) {
      const escaped = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(?:^|\\n)## Extracted from ${escaped}\\s*\\n[\\s\\S]*?(?=\\n## Extracted from |$)`);
    },

    upsertExtractedSection(fileName, text) {
      const section = `## Extracted from ${fileName}\n\n${text.trim()}`;
      const current = (this.form.guidelines || '').trim();
      const regex = this.extractedSectionRegex(fileName);

      if (regex.test(current)) {
        this.form.guidelines = current
          .replace(regex, (match) => (match.startsWith('\n') ? `\n${section}` : section))
          .trim();
        return;
      }

      this.form.guidelines = current ? `${current}\n\n${section}` : section;
    },

    removeExtractedSection(fileName) {
      const current = this.form.guidelines || '';
      this.form.guidelines = current.replace(this.extractedSectionRegex(fileName), '\n').trim();
    },

    createParseRequestId(asset) {
      const assetKey = asset?.id || asset?.localId || 'asset';
      return `${assetKey}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },

    subscribeToBrandGuidelinesParsingChannel() {
      if (!window.Echo || !this.companyId || this.brandGuidelinesSocketChannel) {
        return;
      }

      this.brandGuidelinesSocketChannel = `brand_guidelines.${this.companyId}`;

      window.Echo.channel(this.brandGuidelinesSocketChannel)
        .listen('.brand_guidelines.parsing_completed', (event) => {
          this.handleBrandGuidelinesParsingCompleted(event);
        })
        .listen('.brand_guidelines.parsing_failed', (event) => {
          this.handleBrandGuidelinesParsingFailed(event);
        });
    },

    unsubscribeFromBrandGuidelinesParsingChannel() {
      if (!window.Echo || !this.brandGuidelinesSocketChannel) {
        return;
      }

      window.Echo.leave(this.brandGuidelinesSocketChannel);
      this.brandGuidelinesSocketChannel = null;
    },

    getSocketPayload(event) {
      return event?.data || event || {};
    },

    getAssetByParseRequestId(requestId) {
      const assetKey = this.activeParseRequests[requestId];

      if (!assetKey) {
        return null;
      }

      return this.uploadedAssets.find((asset) => {
        if (asset.localId && asset.localId === assetKey) {
          return true;
        }

        return asset.id && String(asset.id) === String(assetKey);
      }) || null;
    },

    clearParseRequest(requestId) {
      if (!requestId) return;

      this.$delete(this.activeParseRequests, requestId);
    },

    handleBrandGuidelinesParsingCompleted(event) {
      const payload = this.getSocketPayload(event);
      const requestId = payload.request_id;

      // stale broadcast: a parse this session didn't start or has since superseded
      if (!requestId || !this.activeParseRequests[requestId]) {
        return;
      }

      const extractedText = payload.guidelines || '';
      const asset = this.getAssetByParseRequestId(requestId);

      if (asset) {
        this.$set(asset, 'status', 'ready');
        this.$set(asset, 'parseError', '');
        this.$set(asset, 'parseRequestId', null);

        if (extractedText) {
          this.upsertExtractedSection(asset.name, extractedText);
        }
      }

      this.uploadedAssets
        .filter((item) => item.parseRequestId === requestId)
        .forEach((item) => {
          this.$set(item, 'status', 'ready');
          this.$set(item, 'parseRequestId', null);
        });

      this.clearParseRequest(requestId);
      this.hasSocketGuidelinesUpdate = true;
    },

    handleBrandGuidelinesParsingFailed(event) {
      const payload = this.getSocketPayload(event);
      const requestId = payload.request_id;

      if (!requestId || !this.activeParseRequests[requestId]) {
        return;
      }

      const asset = this.getAssetByParseRequestId(requestId);

      if (asset) {
        this.$set(asset, 'status', 'failed');
        this.$set(asset, 'parseError', payload.message || 'Failed to parse file.');
        this.$set(asset, 'parseRequestId', null);
      }

      this.uploadedAssets
        .filter((item) => item.parseRequestId === requestId)
        .forEach((item) => {
          this.$set(item, 'status', 'failed');
          this.$set(item, 'parseError', payload.message || 'Failed to parse file.');
          this.$set(item, 'parseRequestId', null);
        });

      this.clearParseRequest(requestId);
    },

    saveBrandGuidelineDraftForParsing() {
      // concurrent Inserts must share one save — each save uploads every still-local file, so overlap duplicates attachments
      if (!this.pendingDraftSave) {
        this.pendingDraftSave = this.performBrandGuidelineDraftSave().finally(() => {
          this.pendingDraftSave = null;
        });
      }

      return this.pendingDraftSave;
    },

    async performBrandGuidelineDraftSave() {
      const formData = new FormData();

      formData.append('brand_name', this.form.brandName);
      formData.append('guidelines', this.form.guidelines || '');

      this.uploadedAssets
        .filter((asset) => !asset.isExisting && asset.file)
        .forEach((asset) => {
          if (asset.type === 'image') {
            formData.append('images[]', asset.file);
          } else {
            formData.append('documents[]', asset.file);
          }
        });

      this.deletedAttachmentIds.forEach((id) => {
        formData.append('deleted_attachment_ids[]', id);
      });

      this.appendThumbnailToFormData(formData);

      let response;

      if (this.selectedBrandGuideline && this.selectedBrandGuideline.id) {
        formData.append('_method', 'PUT');
        response = await axios.post(`/brand-guidelines/${this.selectedBrandGuideline.id}`, formData);
      } else {
        response = await axios.post('/brand-guidelines', formData);
      }

      const savedBrandGuideline = response.data.data || response.data;
      this.hasSocketGuidelinesUpdate = false;

      this.selectedBrandGuideline = savedBrandGuideline;
      this.form.brandName = savedBrandGuideline.brand_name || this.form.brandName;
      this.form.guidelines = savedBrandGuideline.guidelines || this.form.guidelines || '';

      this.loadSavedAttachments(savedBrandGuideline.attachments || []);
      this.loadThumbnailFromGuideline(savedBrandGuideline);
      this.deletedAttachmentIds = [];

      await this.fetchBrandGuidelines();

      return savedBrandGuideline;
    },

    async insertExtractedContent(asset) {
      if (!asset || asset.status === 'parsing') {
        return;
      }

      if (!this.hasBrandName) {
        return;
      }

      const requestId = this.createParseRequestId(asset);
      const assetKey = asset.localId || String(asset.id || 'brand-guideline');
      const assetName = asset.name;

      this.$set(this.activeParseRequests, requestId, assetKey);
      this.$set(asset, 'status', 'parsing');
      this.$set(asset, 'parseError', '');
      this.$set(asset, 'parseRequestId', requestId);

      try {
        let attachmentId = asset.id;

        if (!this.selectedBrandGuideline?.id || asset.file || this.deletedAttachmentIds.length) {
          await this.saveBrandGuidelineDraftForParsing();
          this.restoreParsingStateAfterReload(requestId, assetName);

          const reloaded = this.uploadedAssets.find((item) => item.name === assetName && item.id);
          attachmentId = reloaded ? reloaded.id : null;
        }

        if (!attachmentId) {
          throw new Error('The file was not saved as an attachment.');
        }

        await axios.post(`/brand-guidelines/${this.selectedBrandGuideline.id}/parse-now`, {
          request_id: requestId,
          attachment_id: attachmentId,
          guidelines: this.form.guidelines || '',
        });
      } catch (error) {
        console.error('Failed to start brand guidelines parsing', error);

        this.handleBrandGuidelinesParsingFailed({
          data: {
            request_id: requestId,
            message: error.response?.data?.message || 'Failed to start file parsing.',
          },
        });
      }
    },

    restoreParsingStateAfterReload(requestId, assetName) {
      const target = this.uploadedAssets.find((item) => item.name === assetName)
        || this.uploadedAssets[this.uploadedAssets.length - 1];

      if (!target) {
        return;
      }

      this.$set(target, 'status', 'parsing');
      this.$set(target, 'parseError', '');
      this.$set(target, 'parseRequestId', requestId);
      this.$set(this.activeParseRequests, requestId, String(target.id ?? target.localId ?? assetName));
    },

    removeBrandAsset(index) {
      const asset = this.uploadedAssets[index];

      if (!asset) return;

      if (asset.localId && this.processingTimeouts[asset.localId]) {
        clearTimeout(this.processingTimeouts[asset.localId]);
        delete this.processingTimeouts[asset.localId];
      }

      if (asset.isExisting && asset.id) {
        this.deletedAttachmentIds.push(asset.id);
      }

      if (!asset.isExisting && asset.url) {
        URL.revokeObjectURL(asset.url);
      }

      this.removeExtractedSection(asset.name);

      this.uploadedAssets.splice(index, 1);
    },

    handleImagesSelected(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => this.addBrandAsset(file));

      if (this.assistantMode && files.length) {
        this.pushMessage(
          'assistant',
          `Added ${files.length} ${files.length === 1 ? 'image' : 'images'}.`
        );
      }

      event.target.value = '';
    },

    detectEditChoice(text) {
      const value = text.toLowerCase().trim();

      if (!value) return null;

      if (
        value.includes('brand') ||
        value.includes('name')
      ) {
        return 'brand_name';
      }

      if (
        value.includes('voice') ||
        value.includes('tone')
      ) {
        return 'voice';
      }

      if (
        value.includes('visual') ||
        value.includes('style') ||
        value.includes('look')
      ) {
        return 'visual_style';
      }

      if (
        value.includes('guideline') ||
        value.includes('rules') ||
        value.includes("do's") ||
        value.includes('dont') ||
        value.includes("don't") ||
        value.includes('photography')
      ) {
        return 'guidelines';
      }

      if (
        value.includes('attachment') ||
        value.includes('document') ||
        value.includes('image') ||
        value.includes('file')
      ) {
        return 'attachments';
      }

      if (
        value.includes('all') ||
        value.includes('everything') ||
        value.includes('whole')
      ) {
        return 'all';
      }

      return 'free_update';
    },

    handleDocumentsSelected(event) {
      const files = Array.from(event.target.files || []);
      files.forEach((file) => this.addBrandAsset(file));

      if (this.assistantMode && files.length) {
        this.pushMessage(
          'assistant',
          `Added ${files.length} ${files.length === 1 ? 'attachment' : 'attachments'}.`
        );
      }

      event.target.value = '';
    },

    async handleUpdate(payload) {
      await axios.put(`/brand-guidelines/${payload.id}`, payload);
      this.fetchBrandGuidelines();
    },
    async handleDelete(brandGuidelineId) {
      await axios.delete(`/brand-guidelines/${brandGuidelineId}`);
      this.fetchBrandGuidelines();
    },
    tryAssistant() {
      this.assistantMode = true;

      if (!this.messages.length) {
        if (this.selectedBrandGuideline) {
          this.assistantStep = 'edit_choice';

          this.pushMessage(
            'assistant',
            `Editing **${this.form.brandName || 'this brand'}**. What would you like to update? You can say: brand name, voice, visual style, guidelines, attachments.`
          );
        } else {
          this.assistantStep = 'brand_name';

          this.pushMessage(
            'assistant',
            `Hi! Let's create a brand style. What's the brand called? (e.g. "Marie Callender's — Frozen")`
          );
        }
      }

      this.$nextTick(() => {
        this.$refs.textarea?.focus();
      });
    },
    getAssistantQuestion() {
      const questions = {
        edit_choice: `What would you like to update? You can say: brand name, voice, visual style, guidelines, attachments, or "all".`,
        edit_more: `Would you like to update another field?`,
        brand_name: `What should the brand name be?`,
        voice: `How would you describe the brand voice in a few words? (e.g. "warm, homemade, comforting")`,
        visual_style: `What visual style should the brand follow? (e.g. "rustic, cozy, golden tones")`,
        guidelines: `Any specific guidelines, do's/don'ts, or photography direction?`,
        attachments: `Would you like to add any attachments — documents or reference images? You can use the buttons below, or type "skip".`,
        ready: `You can save it now, or tell me what else to change.`,
      };

      return questions[this.assistantStep] || questions.edit_choice;
    },
    updateLastAssistantBubble(text) {
      const lastAssistant = [...this.messages]
        .reverse()
        .find((message) => message.type === 'assistant');

      if (lastAssistant) {
        lastAssistant.text = text;
        this.$nextTick(this.scrollToBottom);
        return;
      }

      this.pushMessage('assistant', text);
    },
    resetEditState() {
      Object.values(this.processingTimeouts).forEach((timeoutId) => clearTimeout(timeoutId));
      this.processingTimeouts = {};
      this.activeParseRequests = {};
      this.hasSocketGuidelinesUpdate = false;

      this.uploadedAssets.forEach((asset) => {
        if (!asset.isExisting && asset.url) {
          URL.revokeObjectURL(asset.url);
        }
      });

      this.form = {
        brandName: '',
        voice: '',
        visualStyle: '',
        guidelines: '',
        docs: 0,
        images: 0,
      };

      this.uploadedAssets = [];
      this.isDragOver = false;
      this.deletedAttachmentIds = [];
      this.selectedBrandGuideline = null;
      this.assistantMode = false;
      this.inputText = '';
      this.messages = [];
      this.stagedFiles = [];
      this.isReadyToSave = false;
      this.assistantStep = 'brand_name';
      this.assistantLoading = false;
      this.skippedAssistantSteps = [];
      this.messageId = 0;
      this.resetThumbnailState();
    },
    cancel(options = {}) {
      this.resetEditState();
      this.isEditMode = false;

      if (!options.skipUrlSync) {
        this.syncBrandGuidelineUrl();
      }
    },
    openNewBrandGuideline() {
      this.resetEditState();
      this.isEditMode = true;
      this.syncBrandGuidelineUrl();
    },
    async save() {
      if (!this.canSave) {
        return;
      }

      this.isSaving = true;

      try {
        const formData = new FormData();

        formData.append('brand_name', this.form.brandName);
        formData.append('guidelines', this.form.guidelines || '');

        this.uploadedAssets
          .filter((asset) => !asset.isExisting && asset.file)
          .forEach((asset) => {
            if (asset.type === 'image') {
              formData.append('images[]', asset.file);
            } else {
              formData.append('documents[]', asset.file);
            }
          });

        this.deletedAttachmentIds.forEach((id) => {
          formData.append('deleted_attachment_ids[]', id);
        });

        this.appendThumbnailToFormData(formData);

        let savedBrandGuideline = null;

        if (this.selectedBrandGuideline && this.selectedBrandGuideline.id) {
          formData.append('_method', 'PUT');

          const response = await axios.post(`/brand-guidelines/${this.selectedBrandGuideline.id}`, formData);
          savedBrandGuideline = response.data.data || response.data;
        } else {
          const response = await axios.post('/brand-guidelines', formData);
          savedBrandGuideline = response.data.data || response.data;
        }

        try {
          await this.fetchBrandGuidelines();
        } catch (fetchError) {
          console.error('Failed to refresh brand guidelines', fetchError);
        }

        if (this.fromAnalysis && savedBrandGuideline && savedBrandGuideline.id) {
          this.$emit('update-brand-guidelines-analysis', this.brandGuidelines, [savedBrandGuideline]);
        }

        this.cancel();
        this.$emit('saved');
      } catch (error) {
        console.error('Failed to save brand guidelines', error);
      } finally {
        this.isSaving = false;
      }
    },
    loadSavedAttachments(attachments = []) {
      this.uploadedAssets = [];
      this.deletedAttachmentIds = [];

      attachments.forEach((attachment) => {
        const url = attachment.url + '&lightweight=1&scale_width=300';
        const name = attachment.original_name || attachment.filename || attachment.name || 'Attachment';
        const mimeType = attachment.mime_type || '';
        const isImage = attachment.type === 'image' || mimeType.startsWith('image/');

        this.uploadedAssets.push({
          id: attachment.id,
          localId: null,
          name,
          size: attachment.size || null,
          url: isImage ? url : null,
          file: null,
          isExisting: true,
          type: isImage ? 'image' : 'document',
          mimeType,
          status: 'ready',
          parseError: '',
          parseRequestId: null,
        });
      });
    },
    getAttachmentKey(item, index, type) {
      if (item.id) {
        return `${type}-existing-${item.id}`;
      }

      if (item.localId) {
        return `${type}-local-${item.localId}`;
      }

      return `${type}-index-${index}`;
    },
    pushMessage(type, text, files = []) {
      this.messages.push({ id: ++this.messageId, type, text, files });
      this.$nextTick(this.scrollToBottom);
    },

    applyStepInterpretation(result) {
      if (!result) return;

      if (result.action === 'update' && result.value !== null) {
        if (result.field === 'brand_name') {
          this.form.brandName = result.value || '';
        }

        if (result.field === 'voice') {
          this.form.voice = result.value || '';
        }

        if (result.field === 'visual_style') {
          this.form.visualStyle = result.value || '';
        }

        if (result.field === 'guidelines') {
          this.form.guidelines = result.value || '';
        }
      }
    },

    parseAiJsonResponse(data) {
      const content = data?.content;

      if (!content) {
        throw new Error('AI response content is empty');
      }

      return JSON.parse(content);
    },

    async interpretAssistantStepAnswer(text) {
      const payload = {
        model: 'gpt-5.4',
        reasoning_effort: 'medium',
        messages: [
          {
            role: 'system',
            content: `
You are a Brand Guidelines Assistant.

Create or update a brand guidelines draft from the user's answers.

Important rules:
- Preserve the user's exact intent.
- Do not over-expand or invent brand direction.
- Do not make assumptions from the brand name alone.
- If a field contains "don't change it", "dont change it", "same", "skip", "nothing", "leave it", or similar, return null for that field.
- If action is "skip", value must be null.
- Null means "do not update this field".
- Voice should stay short and comma-separated.
- Visual style should stay short and comma-separated.
- Guidelines should only be generated when the user gave guideline notes or explicitly asked for guidelines.
- Return only valid JSON matching the schema.
- If you move to a field step, either ask the next question in message OR return a short confirmation, but avoid duplicating the frontend question.
- For field steps, next_step should be the next step in this order: brand_name -> voice -> visual_style -> guidelines -> attachments -> generating.
- If the user skips a field, still move to the next step.
- Never return the same current_step as next_step.
If current step is attachments:
- If the user says "skip", "no", "none", or similar, action should be "skip" and next_step should be "generating".
- If the user wants to add files, action should be "choose_field", field should be "attachments", and next_step should be "attachments".
`.trim(),
          },
          {
            role: 'user',
            content: JSON.stringify({
              current_step: this.assistantStep,
              user_answer: text,
              current_record: {
                brand_name: this.form.brandName,
                voice: this.form.voice,
                visual_style: this.form.visualStyle,
                guidelines: this.form.guidelines,
              },
            }),
          },
        ],
        response_format: {
          name: 'brand_guidelines_step_interpretation',
          strict: true,
          schema: {
            type: 'object',
            additionalProperties: false,
            properties: {
              action: {
                type: 'string',
                enum: ['update', 'skip', 'choose_field', 'unclear'],
              },
              field: {
                type: 'string',
                enum: ['brand_name', 'voice', 'visual_style', 'guidelines', 'attachments', 'none'],
              },
              value: {
                anyOf: [
                  { type: 'string' },
                  { type: 'null' },
                ],
              },
              message: {
                type: 'string',
              },
              next_step: {
                type: 'string',
                enum: ['edit_choice', 'brand_name', 'voice', 'visual_style', 'guidelines', 'attachments', 'all', 'generating', 'ready'],
              },
            },
            required: ['action', 'field', 'value', 'message', 'next_step'],
          },
        },
      };

      const { data } = await axios.post('/call-open-ai', payload);

      return this.parseAiJsonResponse(data);
    },

    async send() {
      if (!this.canSend) return;

      const text = this.inputText.trim();

      this.pushMessage('user', text || 'Skipped');

      if (this.assistantStep === 'edit_more') {
        const answer = text.toLowerCase().trim();

        if (
          answer.includes('yes') ||
          answer.includes('another') ||
          answer.includes('more') ||
          answer.includes('update')
        ) {
          this.assistantStep = 'edit_choice';
          this.pushMessage('assistant', this.getAssistantQuestion());
          return;
        }

        this.assistantStep = 'ready';
        this.isReadyToSave = true;

        this.pushMessage(
          'assistant',
          this.getFinalAssistantMessage()
        );

        this.inputText = '';

        return;
      }

      this.inputText = '';
      this.$nextTick(() => this.resetTextarea());

      this.assistantLoading = true;

      this.pushMessage('typing', '');
      const typingId = this.messages[this.messages.length - 1].id;

      try {
        const result = await this.interpretAssistantStepAnswer(text);

        if (this.assistantStep === 'attachments') {
          this.messages = this.messages.filter((message) => message.id !== typingId);

          const assistantMessage = result.message || 'Okay.';
          this.pushMessage('assistant', assistantMessage);

          this.assistantStep = 'generating';
          await this.generateBrandGuidelinesWithAi();
          return;
        }

        this.messages = this.messages.filter((message) => message.id !== typingId);

        const previousStep = this.assistantStep;

        this.applyStepInterpretation(result);

        let nextStep = result.next_step;

        if (['brand_name', 'voice', 'visual_style', 'guidelines', 'attachments'].includes(previousStep)) {
          nextStep = this.getNextStep(previousStep);
        }

        this.assistantStep = nextStep;

        const assistantMessage = result.message || 'Okay.';

        this.pushMessage('assistant', assistantMessage);

        if (
          this.isEditingExistingRecord() &&
          ['brand_name', 'voice', 'visual_style', 'guidelines'].includes(previousStep)
        ) {
          this.assistantStep = 'edit_more';
          this.pushMessage('assistant', this.getAssistantQuestion());
          return;
        }

        if (this.assistantStep === 'attachments') {
          const aiAlreadyAskedQuestion = assistantMessage.includes('?');

          if (!aiAlreadyAskedQuestion) {
            this.pushMessage('assistant', this.getAssistantQuestion());
          }

          return;
        }

        if (this.assistantStep === 'generating') {
          await this.generateBrandGuidelinesWithAi();
          return;
        }

        if (this.assistantStep === 'ready') {
          this.isReadyToSave = true;
          return;
        }

        const aiAlreadyAskedQuestion = assistantMessage.includes('?');

        if (
          ['brand_name', 'voice', 'visual_style', 'guidelines', 'attachments'].includes(this.assistantStep) &&
          !aiAlreadyAskedQuestion
        ) {
          this.pushMessage('assistant', this.getAssistantQuestion());
        }
      } catch (error) {
        console.error('Step interpretation failed:', error);
        console.error('AI error response:', error.response?.data);

        this.messages = this.messages.filter((message) => message.id !== typingId);

        this.pushMessage(
          'assistant',
          'Sorry, I could not understand that. Please try again.'
        );
      } finally {
        this.assistantLoading = false;
      }
    },

    getFinalAssistantMessage() {
      if (this.selectedBrandGuideline && this.selectedBrandGuideline.id) {
        return this.hasFormChanges
          ? 'Done. If everything looks good, click Save to apply the changes.'
          : 'No changes were made. You can update another field or return to the form.';
      }

      return 'Done. If everything looks good, click Save to create these brand guidelines.';
    },

    getNextStep(currentStep) {
      const steps = {
        brand_name: 'voice',
        voice: 'visual_style',
        visual_style: 'guidelines',
        guidelines: 'attachments',
        attachments: 'generating',
      };

      return steps[currentStep] || 'ready';
    },

    appendExtraGuidelines(text) {
      if (!text) return;

      if (this.form.guidelines && this.form.guidelines.trim()) {
        this.form.guidelines = `${this.form.guidelines.trim()}\n\n${text}`;
      } else {
        this.form.guidelines = text;
      }
    },

    async generateBrandGuidelinesWithAi(userInstruction = '') {
      this.assistantLoading = true;

      this.pushMessage('typing', '');
      const typingId = this.messages[this.messages.length - 1].id;

      try {
        const payload = {
          model: 'gpt-5.4',
          reasoning_effort: 'medium',
          messages: [
            {
              role: 'system',
              content: `
You are a Brand Guidelines Assistant.

Create or update a brand guidelines draft from the user's answers.

Important rules:
- Preserve the user's exact intent.
- Do not over-expand or invent brand direction.
- Do not make assumptions from the brand name alone.
- Do not generate content for skipped fields.
- If "guidelines" was skipped, return the existing guidelines value unchanged.
- If existing guidelines are empty and "guidelines" was skipped, return an empty string for guidelines.
- If "visual_style" was skipped, return the existing visual_style unchanged.
- If a field has a value, you may lightly clean it, but do not change its meaning.
- Voice should stay short and comma-separated.
- Visual style should stay short and comma-separated.
- Guidelines should only be generated when the user gave guideline notes or explicitly asked for guidelines.
- Return only valid JSON matching the schema.
`.trim(),
            },
            {
              role: 'user',
              content: JSON.stringify({
                user_instruction: userInstruction,
                current_record: {
                  brand_name: this.form.brandName,
                  voice: this.form.voice,
                  visual_style: this.form.visualStyle,
                  guidelines: this.form.guidelines,
                },
                skipped_steps: this.skippedAssistantSteps,
                attachments: {
                  documents: this.uploadedAssets.filter((asset) => asset.type === 'document').map((doc) => doc.name),
                  images: this.uploadedAssets.filter((asset) => asset.type === 'image').map((image) => image.name),
                },
              }),
            },
          ],
          response_format: {
            name: 'brand_guidelines_assistant_response',
            strict: true,
            schema: {
              type: 'object',
              additionalProperties: false,
              properties: {
                message: {
                  type: 'string',
                },
                fields: {
                  type: 'object',
                  additionalProperties: false,
                  properties: {
                    brand_name: {
                      anyOf: [
                        { type: 'string' },
                        { type: 'null' },
                      ],
                    },
                    voice: {
                      anyOf: [
                        { type: 'string' },
                        { type: 'null' },
                      ],
                    },
                    visual_style: {
                      anyOf: [
                        { type: 'string' },
                        { type: 'null' },
                      ],
                    },
                    guidelines: {
                      anyOf: [
                        { type: 'string' },
                        { type: 'null' },
                      ],
                    },
                  },
                  required: [
                    'brand_name',
                    'voice',
                    'visual_style',
                    'guidelines',
                  ],
                },
              },
              required: [
                'message',
                'fields',
              ],
            },
          },
        };

        const { data } = await axios.post('/call-open-ai', payload);

        this.messages = this.messages.filter((message) => message.id !== typingId);

        const content = data.content || data.message?.content || '';
        const parsed = typeof content === 'string' ? JSON.parse(content) : content;

        if (parsed.fields) {
          this.applyAssistantFields(parsed.fields);
        }

        this.assistantStep = 'ready';
        this.isReadyToSave = true;

        this.pushMessage(
          'assistant',
          this.getFinalAssistantMessage()
        );
        this.inputText = '';
      } catch (error) {
        console.error('Brand guidelines assistant failed', error);

        this.messages = this.messages.filter((message) => message.id !== typingId);

        this.assistantStep = 'ready';
        this.isReadyToSave = true;

        this.pushMessage(
          'assistant',
          this.getFinalAssistantMessage()
        );
      } finally {
        this.assistantLoading = false;
      }
    },

    applyAssistantFields(fields) {
      if (!fields) return;

      if (fields.brand_name !== undefined && fields.brand_name !== null) {
        this.form.brandName = fields.brand_name;
      }

      if (fields.voice !== undefined && fields.voice !== null) {
        this.form.voice = fields.voice;
      }

      if (fields.visual_style !== undefined && fields.visual_style !== null) {
        this.form.visualStyle = fields.visual_style;
      }

      if (fields.guidelines !== undefined && fields.guidelines !== null) {
        this.form.guidelines = fields.guidelines;
      }
    },

    simulateReply(userText, files) {
      this.pushMessage('typing', '');
      const typingId = this.messages[this.messages.length - 1].id;

      setTimeout(() => {
        // Remove typing bubble
        this.messages = this.messages.filter(m => m.id !== typingId);

        // Generate a contextual reply
        let reply = 'Got it! I\'ve noted those changes to the brand guidelines.';
        if (files.length) {
          reply = `I've processed ${files.length} file${files.length > 1 ? 's' : ''}. ` +
            'The brand guidelines have been updated accordingly.';
        }
        if (userText.toLowerCase().includes('save')) {
          reply = 'Everything looks great! Click **Ready To Save** when you\'re done.';
          this.isReadyToSave = true;
        }

        this.pushMessage('assistant', reply);
      }, 1400);
    },

    newline() {
      this.inputText += '\n';
      this.$nextTick(this.autoResize);
    },

    renderText(text) {
      // Simple markdown-like: **bold**
      return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    },

    scrollToBottom() {
      const el = this.$refs.messageList;
      if (el) el.scrollTop = el.scrollHeight;
    },

    triggerFileInput() {
      this.$refs.documentsInput.click();
    },

    triggerImageInput() {
      this.$refs.imagesInput.click();
    },

    autoResize() {
      const ta = this.$refs.textarea;
      if (!ta) return;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
    },

    resetTextarea() {
      const ta = this.$refs.textarea;
      if (ta) ta.style.height = 'auto';
    },
    useBrandGuideline(brandGuideline) {
      if (this.isCreativeStudio) {
        this.$emit('use', {
          kind: 'brand-guidelines',
          id: brandGuideline.id,
          label: brandGuideline.brand_name || brandGuideline.name || 'Brand Guidelines',
          brandGuideline,
        });
        return;
      }
      this.$emit('update-brand-guidelines-analysis', this.brandGuidelines, [brandGuideline]);
    },
  }
};
</script>

<style scoped>
.brand-guidelines {
  --teal: #2eb89a;
  --teal-light: #e8f7f3;
  --teal-mid: #c2ede3;
  --teal-dark:    #25a085;
  --border: #e4e7eb;
  --text-primary: #1a1d23;
  --text-secondary: #6b7280;
  --text-muted: #9ca3af;
  --bg-page: #f8f9fb;
  --bg-card: #ffffff;
  --radius: 8px;
  --radius-sm: 5px;
  --shadow: 0 1px 3px rgba(0,0,0,.07), 0 1px 2px rgba(0,0,0,.05);
  --teal-ring:    rgba(46, 184, 154, 0.20);
  --bg:           #ffffff;
  --bg-soft:      #f7f8fa;
  --bg-msg-user:  #ffffff;
  --text:         #1a1d23;
  --text-light:   #9ca3af;
  --radius-lg:    12px;
  --radius-sm:    5px;
  --teal-dim:     rgba(46, 184, 154, 0.12);
}

.lib-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
}

.lib-subtitle {
  font-size: 13px;
  color: #8a9099;
  line-height: 1.5;
}

.search-wrap {
  position: relative;
  flex: 1 1 320px;
  max-width: 340px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}
.search-input {
  padding-left: 36px;
  border-radius: 9px;
  border: 1px solid #dde2e8;
  font-size: 13.5px;
  font-family: "DM Sans", sans-serif;
  color: #333;
  height: 40px;
}
.search-input:focus {
  border-color: #1aaa8a;
  box-shadow: 0 0 0 3px rgba(26, 170, 138, 0.12);
  outline: none;
}
.search-input::placeholder {
  color: #b0b8c1;
}

.btn-teal {
  background: #1aaa8a;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 13.5px;
  font-weight: 600;
  padding: 8px 16px;
  white-space: nowrap;
  transition: background 0.15s;
}
.btn-teal:hover {
  background: #159475;
  color: #fff;
}
.btn-teal.btn-sm {
  padding: 5px 14px;
  font-size: 13px;
}

.seq-list {
  border: 1px solid #f0f2f5;
  border-radius: 8px;
}

.seq-row {
  padding: 14px 16px;
  border-bottom: 1px solid #f0f2f5;
  cursor: pointer;
  transition: background 0.12s;
  position: relative;
}
.seq-row:hover {
  background: #f8fafb;
}
.seq-row:last-child {
  border-bottom: none;
}

.seq-info {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
}

.seq-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
}

.seq-thumb {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  border: 1px solid #eef1f4;
}

.seq-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.seq-thumb--empty {
  border: 1px dashed #dde2e8;
}

.scope-tag {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 9px;
  border-radius: 20px;
  letter-spacing: 0.01em;
  border: 1px solid #f0f2f5;
}

.seq-slots {
  font-size: 12.5px;
  color: #8a9099;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 480px;
}

.seq-actions {
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.seq-actions.visible {
  opacity: 1;
  pointer-events: all;
}

.icon-btn {
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.icon-btn-danger {
  color: #e53e3e !important;
}
.icon-btn:hover {
  background: #1abc9c;
  color: #fff;
}
.icon-btn-danger:hover {
  background: #1abc9c;
  color: #fff;
}

.empty-state-panel {
  text-align: center;
  padding: 48px 24px;
  color: var(--text-muted);
}

.empty-state-panel__icon {
  width: 40px;
  height: 40px;
  display: block;
  margin: 0 auto 12px;
  opacity: 0.4;
  color: var(--text-muted);
}

.empty-state-panel__title {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.empty-state-panel__desc {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
}

.empty-state-search {
  text-align: center;
  padding: 40px 24px;
  font-size: 13.5px;
  color: var(--text-muted);
}
.btn i {
  margin-top: 0;
}
.content-area {
  margin-top: 0;
}

.edit-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0 0 16px;
}

.btn-edit-back,
.btn-edit-save,
.btn-footer-cancel,
.btn-footer-save,
.btn-use-template,
.btn-insert {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}

.btn-edit-back,
.btn-footer-cancel {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  padding: 6px 10px;
}

.btn-edit-back:hover,
.btn-footer-cancel:hover {
  background: #f3f4f6;
  color: var(--text-primary);
}

.btn-edit-save,
.btn-footer-save {
  background: var(--teal);
  border: none;
  color: #fff;
  padding: 6px 12px;
}

.btn-edit-save:hover:not(:disabled),
.btn-footer-save:hover:not(:disabled) {
  background: var(--teal-dark);
}

.btn-edit-save:disabled,
.btn-footer-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.edit-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

@media (min-width: 992px) {
  .edit-grid {
    grid-template-columns: minmax(0, 1fr) 320px;
  }
}

.edit-main {
  min-width: 0;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Fields ── */
.field-label {
  font-size: .75rem;
  line-height: 1rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 6px;
  display: block;
}

.field-input,
.field-textarea {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  font-size: 13px;
  color: var(--text-primary);
  background: #fff;
  width: 100%;
  padding: 8px 11px;
  transition: border-color 0.15s, box-shadow 0.15s;
  outline: none;
  resize: none;
  box-shadow: none;
}

.field-input:focus,
.field-textarea:focus {
  border-color: var(--teal);
  box-shadow: 0 0 0 3px rgba(46, 184, 154, 0.15);
}

.field-input::placeholder,
.field-textarea::placeholder {
  color: var(--text-muted);
}

.field-textarea--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.6;
  min-height: 280px;
}

.field-hint {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 6px;
}

.assets-label-row,
.guidelines-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.assets-processing-label {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: var(--text-secondary);
}

.assets-brandname-hint {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  color: var(--warning, #b8860b);
}

.thumbnail-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  letter-spacing: inherit;
  color: var(--text-muted);
  font-weight: 300;
  cursor: pointer;
}

.thumbnail-remove-btn:hover {
  color: var(--danger, #dc2626);
}

.upload-dropzone {
  border: 2px dashed var(--border);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.upload-dropzone:hover,
.upload-dropzone--active {
  background: rgba(0, 0, 0, 0.02);
  border-color: #c9cdd4;
}

.upload-dropzone__icon {
  display: block;
  margin: 0 auto 8px;
  color: var(--text-muted);
}

.upload-dropzone__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.upload-dropzone__hint {
  font-size: 11px;
  color: var(--text-muted);
  margin-top: 2px;
}

.asset-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.asset-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
}

.asset-row__icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.asset-row__thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}

.asset-row__info {
  min-width: 0;
  flex: 1;
}

.asset-row__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.asset-row__size {
  font-size: 11px;
  color: var(--text-muted);
}

.asset-row__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.asset-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}

.asset-badge--processing {
  background: rgba(46, 184, 154, 0.12);
  color: var(--teal-dark);
}

.asset-badge--ready {
  background: rgba(46, 184, 154, 0.08);
  border: 1px solid rgba(46, 184, 154, 0.35);
  color: var(--teal-dark);
}
.asset-badge--failed {
  background: #fde8e8;
  border: 1px solid rgba(229, 62, 62, 0.3);
  color: #e53e3e;
}

.btn-insert {
  background: #fff;
  border: none;
  color: var(--text-primary);
  font-size: 12px;
  padding: 5px 10px;
}

.btn-insert:hover {
  background: #1aaa8a;
  border: none;
  color: #fff;
}

.btn-insert:disabled,
.btn-insert:disabled:hover {
  background: #fff;
  color: var(--text-secondary);
  opacity: 0.5;
  cursor: not-allowed;
}

.asset-row__remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.15s, color 0.15s, background 0.15s;
}

.asset-row:hover .asset-row__remove {
  opacity: 1;
}

.asset-row__remove:hover {
  color: #ef4444;
  background: #fef2f2;
}

.btn-use-template {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 11px;
  padding: 4px 8px;
}

.btn-use-template:hover {
  background: #f3f4f6;
  color: var(--text-primary);
}

/* ── Buttons ── */
.btn-assistant {
  display: flex;
  align-items: center;
  font-size: .875rem;
  line-height: 1.25rem;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;
  font-weight: 500;
}

.btn-assistant:hover {
  background: #189A73;
  border-color: #189A73;
  color: #fff;
}

.btn-attachment {
  display: flex;
  align-items: center;
  font-size: 12.5px;
  color: var(--text-secondary);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.btn-attachment:hover {
  background: #f3f4f6;
  border-color: #c9cdd4;
  color: var(--text-primary);
}

.live-preview {
  align-self: start;
}

@media (min-width: 992px) {
  .live-preview {
    position: sticky;
    top: 16px;
  }
}

.live-preview__card {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.02);
  overflow: hidden;
}

.live-preview__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
  background: #fff;
}

.live-preview__title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}

.live-preview__badge {
  margin-left: auto;
  font-size: 10px;
  padding: 2px 8px;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-secondary);
}

.live-preview__body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.live-preview__section {
  min-width: 0;
}

.live-preview__label--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.live-preview__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 6px;
}

.live-preview__brand {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.live-preview__brand--empty {
  font-weight: 400;
  font-style: italic;
  color: var(--text-muted);
}

.live-preview__hero {
  aspect-ratio: 16 / 9;
  border: 1px dashed var(--border);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.live-preview__hero img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.live-preview__hero--empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: var(--text-muted);
  font-size: 10px;
}

.live-preview__colors {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
}

.live-preview__color {
  display: flex;
  align-items: center;
  gap: 8px;
  width: calc(50% - 6px);
}

.live-preview__swatch {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.live-preview__hex {
  font-size: 11px;
  color: var(--text-secondary);
  font-family: ui-monospace, monospace;
}

.live-preview__typography {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.live-preview__typo-line {
  font-size: 12px;
  color: var(--text-primary);
}

.live-preview__empty-hint {
  font-size: 11px;
  color: var(--text-muted);
  font-style: italic;
}

.live-preview__stats {
  display: flex;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: var(--text-muted);
}

.edit-sticky-footer {
  position: sticky;
  bottom: 0;
  margin-top: 12px;
  padding-top: 12px;
  background: linear-gradient(to top, #fff 70%, rgba(255, 255, 255, 0));
}

.edit-sticky-footer__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  padding: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.edit-sticky-footer__hint {
  font-size: 12px;
  color: var(--text-muted);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.edit-sticky-footer__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-footer-cancel,
.btn-footer-save {
  height: 36px;
  padding: 0 12px;
}

.mr-1 { margin-right: 4px; }
.brand-guidelines-loading {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}
.bga-shell {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 580px;
  overflow: hidden;
}

/* ── Header ── */
.bga-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 18px;
  border-bottom: 1px solid var(--border);
  background: var(--bg);
  flex-shrink: 0;
}

.bga-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bga-header__icon {
  display: flex;
  align-items: center;
  color: var(--teal);
}

.bga-header__title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -.15px;
}

.bga-header__badge {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: .04em;
  color: #fff;
  background: var(--teal);
  border-radius: 99px;
  padding: 2px 8px;
  line-height: 1.5;
}

.bga-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ── Buttons ── */
.bga-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 500;
  padding: 5px 13px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
  white-space: nowrap;
  line-height: 1.4;
}

.bga-btn--ready {
  background: var(--bg);
  border-color: var(--border);
  color: var(--text-muted);
}
.bga-btn--ready:hover {
  border-color: var(--teal);
  color: var(--teal);
  background: var(--teal-dim);
}

.bga-btn--outline {
  background: var(--bg);
  border-color: var(--border);
  color: var(--text);
}
.bga-btn--outline:hover {
  background: var(--teal);
  color: #fff;
}

.bga-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 8px;
  scroll-behavior: smooth;
}

.bga-messages__inner {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bga-messages::-webkit-scrollbar { width: 4px; }
.bga-messages::-webkit-scrollbar-thumb { background: var(--border); border-radius: 99px; }

/* ── Message rows ── */
.bga-msg {
  display: flex;
}

.bga-msg--assistant { justify-content: flex-start; }
.bga-msg--user      { justify-content: flex-end; }
.bga-msg--typing    { justify-content: flex-start; }

/* ── Bubbles ── */
.bga-msg__bubble {
  max-width: 72%;
  padding: 9px 13px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.6;
}

.bga-msg--assistant .bga-msg__bubble {
  background: var(--bg-soft);
  color: var(--text);
  border-bottom-left-radius: 3px;
}

.bga-msg--user .bga-msg__bubble {
  background: var(--teal-dark);
  color: #fff;
  border-bottom-right-radius: 3px;
}

.bga-msg__text { margin: 0; }
.bga-msg__text strong { font-weight: 600; color: var(--text); }

/* Attached files inside bubble */
.bga-msg__files {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 7px;
}

.bga-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-muted);
  background: rgba(0,0,0,.04);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 2px 7px;
}

/* ── Typing indicator ── */
.bga-msg__bubble--typing {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 14px;
  background: var(--bg-soft);
  border-bottom-left-radius: 3px;
}

.bga-typing-dot {
  width: 6px;
  height: 6px;
  background: var(--text-light);
  border-radius: 50%;
  animation: bgaTyping 1.2s infinite ease-in-out;
}
.bga-typing-dot:nth-child(2) { animation-delay: .2s; }
.bga-typing-dot:nth-child(3) { animation-delay: .4s; }

@keyframes bgaTyping {
  0%, 60%, 100% { transform: translateY(0); opacity: .4; }
  30%            { transform: translateY(-4px); opacity: 1; }
}

/* ── Input bar ── */
.bga-input-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 14px;
  border-top: 1px solid var(--border);
  background: var(--bg);
  flex-shrink: 0;
  flex-wrap: nowrap;
}

/* Tool buttons */
.bga-tool-btn {
  width: 34px;
  height: 34px;
  min-width: 34px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
}

.bga-tool-btn svg,
.bga-send-btn svg {
  display: block;
}

.bga-tool-btn:hover {
  background: var(--teal);
  color: #fff;
}

/* Staged files */
.bga-staged-files {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  width: 100%;
  order: -1;
  padding-bottom: 6px;
}

.bga-staged-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 500;
  color: var(--teal-dark);
  background: var(--teal-dim);
  border: 1px solid var(--teal-ring);
  border-radius: 99px;
  padding: 3px 8px 3px 10px;
  max-width: 180px;
}
.bga-staged-chip span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bga-staged-chip__remove {
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--teal-dark);
  opacity: .6;
  transition: opacity .12s;
}
.bga-staged-chip__remove:hover { opacity: 1; }

/* Textarea wrapper */
.bga-input-wrap {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.bga-textarea {
  width: 100%;
  min-height: 34px;
  max-height: 120px;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  resize: none;
  overflow-y: auto;
  line-height: 18px;
  font-size: 13px;
  box-sizing: border-box;
  display: block;
}
.bga-textarea::placeholder { color: var(--text-light); }

.bga-textarea:focus,
.bga-textarea:focus-visible {
  outline-color: var(--teal);
}

/* Send button */
.bga-send-btn {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--border);
  border: none;
  border-radius: var(--radius-sm);
  color: #fff;
  cursor: not-allowed;
  transition: background .14s, box-shadow .14s, transform .1s;
}

.bga-send-btn--active {
  background: var(--teal);
  cursor: pointer;
}
.bga-send-btn--active:hover {
  background: var(--teal-dark);
  box-shadow: 0 2px 8px rgba(46,184,154,.35);
}
.bga-send-btn--active:active { transform: scale(.94); }

/* Hidden inputs */
.bga-hidden-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

/* ── Transitions ── */
.bga-fade-enter-active, .bga-fade-leave-active { transition: opacity .2s; }
.bga-fade-enter, .bga-fade-leave-to { opacity: 0; }

.bga-msg-enter-active { transition: opacity .2s ease, transform .2s ease; }
.bga-msg-enter { opacity: 0; transform: translateY(8px); }

.bga-btn--ready-save {
  font-size: 10px;
  color: #0f172a;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
}
</style>
